import { useState } from 'react'
import { admin } from '../api/client'
import type { AdminStation, IngestRun } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { formatDate, formatDateTime } from '../lib/time'
import { latestPerStation, runCounts, runKind, runWindow, statusPill } from '../lib/runs'

const KIND_OPTIONS = ['schedule', 'reconcile', 'audit', 'backfill', 'manual']

interface Props {
  stations: AdminStation[] | undefined
}

/** What the worker has been doing: every collection, sweep, audit and backfill.
 *
 *  The last monthly audit per station sits on top because it answers the one
 *  question nothing else on screen does — is the archive still holding
 *  everything the vendors serve? It is fetched separately, filtered to audits,
 *  so it cannot scroll away under a month of daily runs. */
export function RunsLog({ stations }: Props) {
  const [station, setStation] = useState('')
  const [kind, setKind] = useState('')

  const audits = useAsync(() => admin.runs({ trigger: 'audit', limit: 50 }), [])
  const runs = useAsync(
    () => admin.runs({ limit: 100, station: station || undefined,
                       trigger: kind || undefined }),
    [station, kind])

  const names = new Map(stations?.map((s) => [s.code, s.name]))
  const nameOf = (code: string | null) => (code ? names.get(code) ?? code : '—')
  // By name, so the list does not reshuffle as audits finish in turn.
  const lastAudits = latestPerStation(audits.data ?? [])
    .sort((a, b) => nameOf(a.station).localeCompare(nameOf(b.station)))

  return (
    <>
      <h3>Last archive audit</h3>
      {audits.loading && <p className="muted">Loading…</p>}
      {audits.error && <p className="error">{audits.error}</p>}
      {audits.data && lastAudits.length === 0 && (
        <p className="muted">No audit has run yet. The worker runs one per
          station every 30 days.</p>
      )}
      {lastAudits.length > 0 && (
        <table className="grid">
          <tbody>
            {lastAudits.map((run) => (
              <tr key={run.id}>
                <td className="nowrap"><strong>{nameOf(run.station)}</strong></td>
                <td className="nowrap"><RunStatus status={run.status} /></td>
                <td className="muted nowrap">{formatDate(run.started_at)}</td>
                <td className="detail">{run.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="section-head runs-head">
        <h3>Recent runs</h3>
        <div className="panel-controls">
          <label className="field inline">
            <span>Station</span>
            <select value={station} onChange={(e) => setStation(e.target.value)}>
              <option value="">all</option>
              {stations?.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </label>
          <label className="field inline">
            <span>Kind</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">all</option>
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>{runKind(k)}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {runs.loading && <p className="muted">Loading…</p>}
      {runs.error && <p className="error">{runs.error}</p>}
      {runs.data?.length === 0 && <p className="muted">No runs match.</p>}
      {runs.data && runs.data.length > 0 && (
        <table className="grid">
          <thead>
            <tr>
              <th>Started</th><th>Station</th><th>Kind</th><th>Window</th>
              <th>Status</th><th>Readings</th><th>Details</th>
            </tr>
          </thead>
          <tbody>
            {runs.data.map((run) => (
              <tr key={run.id}>
                <td className="muted nowrap">{formatDateTime(run.started_at)}</td>
                <td className="nowrap">{nameOf(run.station)}</td>
                <td className="nowrap">{runKind(run.trigger)}</td>
                <td className="nowrap">{runWindow(run)}</td>
                <td className="nowrap"><RunStatus status={run.status} /></td>
                <td className="nowrap">{runCounts(run)}</td>
                <td className="detail">{run.error ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/** The status word, in a monochrome pill only when it needs weight. */
function RunStatus({ status }: { status: IngestRun['status'] }) {
  const pill = statusPill(status)
  return pill ? <span className={`pill ${pill}`}>{status}</span> : <>{status}</>
}
